import { useMemo } from 'react';
import { CommanderAPI } from '../CommanderAPI';
import useMyWorkspacesChildren from '../../../../redux/workspace/hooks/useMyWorkspacesChildren';
import useMultipleQueries from '../../../../shared/util/rtk/useQueries';
import { DeviceStatusAirConnectSummaryAPIResponse } from '../deviceStatus.model';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

type AggregatedResult = {
  isUninitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  currentData?: DeviceStatusAirConnectSummaryAPIResponse;
  data?: DeviceStatusAirConnectSummaryAPIResponse;
  error?: unknown;
};

const EMPTY_SUMMARY: DeviceStatusAirConnectSummaryAPIResponse = {
  shellyRemoved: 0,
  gatewayLost: 0,
  gatewayOk: 0,
  deviceCount: 0,
  mqttDisconnected: 0,
  nodeCount: 0,
  nodeLost: 0,
  nodeOk: 0,
  gatewayTimeout: 0,
};

function useAggregatedGetAirConnectStatusCountBySubworkspaces(
  enabled = true
): AggregatedResult {
  const { data: childWorkspacesDict } = useMyWorkspacesChildren({
    skip: !enabled,
  });

  const childWorkspaceIds = useMemo(
    () => (childWorkspacesDict ? Object.keys(childWorkspacesDict) : []),
    [childWorkspacesDict]
  );

  const childWorkspaceResponses = useMultipleQueries<
    DeviceStatusAirConnectSummaryAPIResponse | undefined,
    string
  >(
    CommanderAPI.endpoints.getAirConnectStatusCountByWorkspace,
    childWorkspaceIds,
    {
      pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
      refetchOnMountOrArgChange: true,
      skip: !enabled || !childWorkspaceIds.length,
    }
  );

  const aggregatedCurrentData = useMemo<
    DeviceStatusAirConnectSummaryAPIResponse | undefined
  >(() => {
    if (!enabled || !childWorkspaceIds.length) {
      return EMPTY_SUMMARY;
    }

    const summaries =
      childWorkspaceResponses.currentData ?? childWorkspaceResponses.data;
    if (!summaries) {
      return undefined;
    }

    return summaries.reduce<DeviceStatusAirConnectSummaryAPIResponse>(
      (accumulator, summary) => ({
        shellyRemoved:
          accumulator.shellyRemoved + (summary?.shellyRemoved ?? 0),
        gatewayLost: accumulator.gatewayLost + (summary?.gatewayLost ?? 0),
        gatewayOk: accumulator.gatewayOk + (summary?.gatewayOk ?? 0),
        deviceCount: accumulator.deviceCount + (summary?.deviceCount ?? 0),
        mqttDisconnected:
          accumulator.mqttDisconnected + (summary?.mqttDisconnected ?? 0),
        nodeCount: accumulator.nodeCount + (summary?.nodeCount ?? 0),
        nodeLost: accumulator.nodeLost + (summary?.nodeLost ?? 0),
        nodeOk: accumulator.nodeOk + (summary?.nodeOk ?? 0),
        gatewayTimeout:
          accumulator.gatewayTimeout + (summary?.gatewayTimeout ?? 0),
      }),
      {
        ...EMPTY_SUMMARY,
      }
    );
  }, [
    childWorkspaceIds.length,
    childWorkspaceResponses.currentData,
    childWorkspaceResponses.data,
    enabled,
  ]);

  return useMemo(
    () => ({
      ...childWorkspaceResponses,
      currentData: aggregatedCurrentData,
      data: aggregatedCurrentData,
      isUninitialized: !enabled || childWorkspaceResponses.isUninitialized,
    }),
    [aggregatedCurrentData, childWorkspaceResponses, enabled]
  );
}

export default useAggregatedGetAirConnectStatusCountBySubworkspaces;
